import { metersToPixels } from '../constants.ts';
import { tessellateRing } from '../map/ring.ts';
import type { MapDocument } from '../map/types.ts';
import type { Vec2 } from '../types.ts';

/** 2 m. Fine enough that a sample every half cell catches any shore the path really crosses. */
export const RASTER_CELL_PX = metersToPixels(2);

/**
 * Islands and rocks as a solid / water grid, plus clearance (px to the nearest solid or the map
 * edge) at each cell. Cells an outline only cuts through are solid, so the grid errs toward land:
 * a path the raster calls clear may still need the exact check, never the other way round.
 */
export type Raster = {
  cell: number;
  cols: number;
  rows: number;
  width: number;
  height: number;
  solid: Uint8Array;
  clearance: Float32Array;
};

const FAR = 1e20;

function fillOutline(raster: Raster, outline: readonly Vec2[]): void {
  const { cell, cols, rows, solid } = raster;
  const count = outline.length;

  if (count < 3) {
    return;
  }

  let minY = Infinity;
  let maxY = -Infinity;

  for (const point of outline) {
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }

  const rowFrom = Math.max(0, Math.floor(minY / cell));
  const rowTo = Math.min(rows - 1, Math.floor(maxY / cell));
  const crossings: number[] = [];

  // Even-odd scanline through each row's centre: the same rule `pointInPolygon` applies.
  for (let row = rowFrom; row <= rowTo; row += 1) {
    const y = (row + 0.5) * cell;
    crossings.length = 0;

    for (let i = 0; i < count; i += 1) {
      const a = outline[i]!;
      const b = outline[(i + 1) % count]!;

      if (a.y > y !== b.y > y) {
        crossings.push(a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x));
      }
    }

    crossings.sort((left, right) => left - right);

    for (let i = 0; i + 1 < crossings.length; i += 2) {
      const colFrom = Math.max(0, Math.ceil(crossings[i]! / cell - 0.5));
      const colTo = Math.min(cols - 1, Math.floor(crossings[i + 1]! / cell - 0.5));

      for (let col = colFrom; col <= colTo; col += 1) {
        solid[row * cols + col] = 1;
      }
    }
  }

  // Then every cell the outline itself passes through, so a thin spit is never skipped.
  const step = cell / 4;

  for (let i = 0; i < count; i += 1) {
    const a = outline[i]!;
    const b = outline[(i + 1) % count]!;
    const span = Math.hypot(b.x - a.x, b.y - a.y);
    const samples = Math.max(1, Math.ceil(span / step));

    for (let s = 0; s <= samples; s += 1) {
      const t = s / samples;
      const col = Math.floor((a.x + (b.x - a.x) * t) / cell);
      const row = Math.floor((a.y + (b.y - a.y) * t) / cell);

      if (col >= 0 && row >= 0 && col < cols && row < rows) {
        solid[row * cols + col] = 1;
      }
    }
  }
}

/** Felzenszwalb–Huttenlocher 1-D squared distance transform, in place over `f` at `stride`. */
function transform1d(
  f: Float64Array,
  offset: number,
  stride: number,
  n: number,
  scratch: { d: Float64Array; v: Int32Array; z: Float64Array },
): void {
  const { d, v, z } = scratch;
  let k = 0;
  v[0] = 0;
  z[0] = -Infinity;
  z[1] = Infinity;

  for (let q = 1; q < n; q += 1) {
    const fq = f[offset + q * stride]! + q * q;
    let s: number;

    // z[0] is -Infinity, so k never drops below 0.
    for (;;) {
      const p = v[k]!;
      s = (fq - (f[offset + p * stride]! + p * p)) / (2 * q - 2 * p);

      if (s > z[k]!) {
        break;
      }

      k -= 1;
    }

    k += 1;
    v[k] = q;
    z[k] = s;
    z[k + 1] = Infinity;
  }

  k = 0;

  for (let q = 0; q < n; q += 1) {
    while (z[k + 1]! < q) {
      k += 1;
    }

    const p = v[k]!;
    d[q] = (q - p) * (q - p) + f[offset + p * stride]!;
  }

  for (let q = 0; q < n; q += 1) {
    f[offset + q * stride] = d[q]!;
  }
}

function computeClearance(raster: Raster): void {
  const { cols, rows, cell, solid, clearance } = raster;
  const f = new Float64Array(cols * rows);

  for (let i = 0; i < f.length; i += 1) {
    f[i] = solid[i] ? 0 : FAR;
  }

  const n = Math.max(cols, rows);
  const scratch = {
    d: new Float64Array(n),
    v: new Int32Array(n),
    z: new Float64Array(n + 1),
  };

  for (let col = 0; col < cols; col += 1) {
    transform1d(f, col, cols, rows, scratch);
  }

  for (let row = 0; row < rows; row += 1) {
    transform1d(f, row * cols, 1, cols, scratch);
  }

  for (let row = 0; row < rows; row += 1) {
    const toEdgeY = Math.min(row + 0.5, rows - row - 0.5) * cell;

    for (let col = 0; col < cols; col += 1) {
      const index = row * cols + col;
      const toEdge = Math.min(toEdgeY, Math.min(col + 0.5, cols - col - 0.5) * cell);
      const toLand = f[index]! >= FAR ? Infinity : Math.sqrt(f[index]!) * cell;
      clearance[index] = solid[index] ? 0 : Math.min(toEdge, toLand);
    }
  }
}

export function buildRaster(map: MapDocument, cell: number = RASTER_CELL_PX): Raster {
  const cols = Math.max(1, Math.ceil(map.bounds.width / cell));
  const rows = Math.max(1, Math.ceil(map.bounds.height / cell));
  const raster: Raster = {
    cell,
    cols,
    rows,
    width: map.bounds.width,
    height: map.bounds.height,
    solid: new Uint8Array(cols * rows),
    clearance: new Float32Array(cols * rows),
  };

  for (const ring of [...map.islands, ...map.rocks]) {
    fillOutline(raster, tessellateRing(ring));
  }

  computeClearance(raster);
  return raster;
}

/** Solid at a world point. Off the map is solid, as `pointInSolid` treats the bounds. */
export function rasterSolidAt(raster: Raster, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= raster.width || y >= raster.height) {
    return true;
  }

  const col = Math.floor(x / raster.cell);
  const row = Math.floor(y / raster.cell);

  if (col >= raster.cols || row >= raster.rows) {
    return true;
  }

  return raster.solid[row * raster.cols + col] === 1;
}

/** Px of open water around a world point; 0 on land or off the map. */
export function rasterClearanceAt(raster: Raster, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= raster.width || y >= raster.height) {
    return 0;
  }

  const col = Math.min(raster.cols - 1, Math.floor(x / raster.cell));
  const row = Math.min(raster.rows - 1, Math.floor(y / raster.cell));
  return raster.clearance[row * raster.cols + col]!;
}

/**
 * Px of water from a point along a heading before the first solid cell (or the map edge),
 * marched every half cell and given up at `maxDistance`.
 */
export function rasterClearAhead(
  raster: Raster,
  x: number,
  y: number,
  heading: number,
  maxDistance: number,
): number {
  const step = raster.cell / 2;
  const dx = Math.cos(heading) * step;
  const dy = Math.sin(heading) * step;
  let travelled = 0;
  let px = x;
  let py = y;

  // A boat parked on the shoreline sits in a solid edge cell; measure from where it leaves it.
  while (travelled < maxDistance && rasterSolidAt(raster, px, py)) {
    px += dx;
    py += dy;
    travelled += step;
  }

  const from = travelled;

  while (travelled < maxDistance) {
    px += dx;
    py += dy;
    travelled += step;

    if (rasterSolidAt(raster, px, py)) {
      return travelled - from;
    }
  }

  return maxDistance;
}
